import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  SupplierInvoiceWithRelations,
  SupplierInvoiceLineWithRelations,
  SupplierInvoiceUpdateData,
  SupplierInvoiceLineFormData,
  SupplierInvoiceFilters,
  CreateSupplierInvoiceData,
  SupplierInvoice,
} from '../types'

const QUERY_KEY = 'supplier_invoices'
const LINES_QUERY_KEY = 'supplier_invoice_lines'

/**
 * Hook pour récupérer les factures fournisseurs d'une résidence
 */
export function useSupplierInvoices(filters: SupplierInvoiceFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: async (): Promise<SupplierInvoiceWithRelations[]> => {
      let query = supabase
        .from('supplier_invoices')
        .select(`
          *,
          suppliers(id, code, name),
          contracts(id, reference, label)
        `)
        .eq('residence_id', filters.residence_id)
        .order('invoice_date', { ascending: false })

      if (filters.fiscal_year_id) {
        query = query.eq('fiscal_year_id', filters.fiscal_year_id)
      }

      if (filters.supplier_id) {
        query = query.eq('supplier_id', filters.supplier_id)
      }

      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      if (filters.payment_status) {
        query = query.eq('payment_status', filters.payment_status)
      }

      if (filters.search) {
        query = query.or(
          `invoice_number.ilike.%${filters.search}%,internal_reference.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
        )
      }

      if (filters.due_date_from) {
        query = query.gte('due_date', filters.due_date_from)
      }

      if (filters.due_date_to) {
        query = query.lte('due_date', filters.due_date_to)
      }

      const { data, error } = await query
      if (error) throw error
      return data as SupplierInvoiceWithRelations[]
    },
    enabled: !!filters.residence_id,
  })
}

/**
 * Hook pour récupérer le détail d'une facture avec ses lignes
 */
export function useSupplierInvoice(invoiceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', invoiceId],
    queryFn: async (): Promise<SupplierInvoiceWithRelations> => {
      const { data, error } = await supabase
        .from('supplier_invoices')
        .select(`
          *,
          suppliers(*),
          contracts(id, reference, label),
          supplier_invoice_lines(
            *,
            residence_chart_accounts(
              id,
              custom_code,
              custom_name,
              chart_accounts(id, code, name)
            )
          )
        `)
        .eq('id', invoiceId!)
        .single()

      if (error) throw error
      return data as SupplierInvoiceWithRelations
    },
    enabled: !!invoiceId,
  })
}

/**
 * Hook pour récupérer les lignes d'une facture
 */
export function useSupplierInvoiceLines(invoiceId?: string) {
  return useQuery({
    queryKey: [LINES_QUERY_KEY, invoiceId],
    queryFn: async (): Promise<SupplierInvoiceLineWithRelations[]> => {
      const { data, error } = await supabase
        .from('supplier_invoice_lines')
        .select(`
          *,
          residence_chart_accounts(
            id,
            custom_code,
            custom_name,
            chart_accounts(id, code, name)
          ),
          distribution_keys(id, name, key_type)
        `)
        .eq('invoice_id', invoiceId!)
        .order('line_number')

      if (error) throw error
      return data as SupplierInvoiceLineWithRelations[]
    },
    enabled: !!invoiceId,
  })
}

/**
 * Hook pour créer une facture avec ses lignes
 */
export function useCreateSupplierInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ invoice, lines }: CreateSupplierInvoiceData) => {
      // Créer la facture
      const { data: invoiceResult, error: invoiceError } = await supabase
        .from('supplier_invoices')
        .insert({
          ...invoice,
          internal_reference: '', // auto-généré par trigger
          status: 'DRAFT',
          payment_status: 'UNPAID',
          amount_paid: 0,
          amount_remaining: invoice.total_ttc,
        })
        .select()
        .single()

      if (invoiceError) throw invoiceError

      // Créer les lignes
      if (lines.length > 0) {
        const linesWithInvoiceId = lines.map((line, index) => ({
          ...line,
          invoice_id: invoiceResult.id,
          line_number: index + 1,
        }))

        const { error: linesError } = await supabase
          .from('supplier_invoice_lines')
          .insert(linesWithInvoiceId)

        if (linesError) throw linesError
      }

      return invoiceResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour mettre à jour une facture (brouillon uniquement)
 */
export function useUpdateSupplierInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SupplierInvoiceUpdateData }) => {
      const { data: result, error } = await supabase
        .from('supplier_invoices')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour valider une facture (génère les écritures comptables)
 */
export function useValidateSupplierInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data, error } = await supabase.rpc('validate_supplier_invoice', {
        p_invoice_id: invoiceId,
      })

      if (error) throw error
      return data as string // ID de la transaction créée
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

/**
 * Hook pour annuler une facture
 */
export function useCancelSupplierInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      // Pour l'instant, mise à jour simple du statut
      // TODO: Implémenter la contre-passation comptable
      const { data, error } = await supabase
        .from('supplier_invoices')
        .update({ status: 'CANCELLED' })
        .eq('id', invoiceId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour supprimer une facture (brouillon uniquement)
 */
export function useDeleteSupplierInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('supplier_invoices').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour récupérer les factures impayées d'une résidence (pour préparation règlement)
 */
type UnpaidInvoice = Pick<SupplierInvoice, 'id' | 'invoice_number' | 'internal_reference' | 'supplier_id' | 'invoice_date' | 'due_date' | 'total_ttc' | 'amount_paid' | 'amount_remaining' | 'payment_status'> & { suppliers: { id: string; code: string; name: string } | null }

export function useUnpaidSupplierInvoices(residenceId?: string, supplierId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'unpaid', residenceId, supplierId],
    queryFn: async (): Promise<UnpaidInvoice[]> => {
      let query = supabase
        .from('supplier_invoices')
        .select(`
          id,
          invoice_number,
          internal_reference,
          supplier_id,
          invoice_date,
          due_date,
          total_ttc,
          amount_paid,
          amount_remaining,
          payment_status,
          suppliers(id, code, name)
        `)
        .eq('residence_id', residenceId!)
        .eq('status', 'VALIDATED')
        .neq('payment_status', 'PAID')
        .order('due_date')

      if (supplierId) {
        query = query.eq('supplier_id', supplierId)
      }

      const { data, error } = await query
      if (error) throw error
      return data as UnpaidInvoice[]
    },
    enabled: !!residenceId,
  })
}

/**
 * Hook pour ajouter une ligne à une facture brouillon
 */
export function useAddSupplierInvoiceLine() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ invoiceId, line }: { invoiceId: string; line: Omit<SupplierInvoiceLineFormData, 'invoice_id' | 'line_number'> }) => {
      // Récupérer le prochain numéro de ligne
      const { data: existing } = await supabase
        .from('supplier_invoice_lines')
        .select('line_number')
        .eq('invoice_id', invoiceId)
        .order('line_number', { ascending: false })
        .limit(1)

      const nextLineNumber = existing?.[0]?.line_number ? existing[0].line_number + 1 : 1

      const { data, error } = await supabase
        .from('supplier_invoice_lines')
        .insert({
          ...line,
          invoice_id: invoiceId,
          line_number: nextLineNumber,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [LINES_QUERY_KEY, variables.invoiceId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', variables.invoiceId] })
    },
  })
}

/**
 * Hook pour supprimer une ligne de facture
 */
export function useDeleteSupplierInvoiceLine() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, invoiceId }: { id: string; invoiceId: string }) => {
      const { error } = await supabase.from('supplier_invoice_lines').delete().eq('id', id)
      if (error) throw error
      return invoiceId
    },
    onSuccess: (invoiceId) => {
      queryClient.invalidateQueries({ queryKey: [LINES_QUERY_KEY, invoiceId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', invoiceId] })
    },
  })
}
